import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { authService } from '../../services';
import { isValidEmail, isStrongPassword } from '../../utils/auth';
import routes from '../../utils/routes';
import './Register.css';

const Register = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Form validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required('First name is required'),
    lastName: Yup.string()
      .required('Last name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
      .test('is-valid-email', 'Invalid email format', isValidEmail),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required'),
  });

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!agreeToTerms) {
        setServerError('You must agree to the terms and conditions');
        return;
      }

      try {
        setIsLoading(true);
        setServerError('');
        
        // Create a unique username by combining first name, last name and timestamp
        const timestamp = new Date().getTime().toString().slice(-4);
        const usernameBase = `${values.firstName.toLowerCase()}${values.lastName.toLowerCase()}`;
        
        // According to API docs, prepare the registration data with the exact expected fields
        const userData = {
          username: `${usernameBase}${timestamp}`, // Make username unique 
          email: values.email,
          password: values.password
        };
        
        console.log('Sending registration data:', userData);
        
        try {
          // Call register API using the new authService
          const response = await authService.register(userData);
          console.log('Registration success:', response.data);
          
          // The API might not explicitly include a requireOTP field
          // Always navigate to the OTP verification page after registration
          navigate(routes.public.VERIFY_OTP, { 
            state: { 
              email: values.email,
              isLogin: false
            } 
          });
        } catch (apiError) {
          console.error('API Error:', apiError);
          
          if (apiError.response) {
            console.error('Response data:', apiError.response.data);
            console.error('Response status:', apiError.response.status);
            
            const errorMessage = apiError.response.data?.message || 
                                apiError.response.data?.error ||
                                'An error occurred during registration.';
                                
            // If the server returns validation errors, display them
            if (apiError.response.data?.errors) {
              const errors = apiError.response.data.errors;
              const errorMessages = Array.isArray(errors) 
                ? errors.map(e => e.message || e).join(', ')
                : 'Validation failed. Please check your information.';
              
              setServerError(errorMessages);
            } else {
              setServerError(errorMessage);
            }
          } else {
            setServerError('An error occurred during registration. Please try again.');
          }
        }
      } catch (error) {
        console.error('Registration error:', error);
        setServerError(
          'An unexpected error occurred. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <div className="register-title">Create an Account</div>
          <div className="register-subtitle">Join our trading platform</div>
        </div>

        {serverError && (
          <div className="error-message" style={{ marginBottom: 'var(--spacing-md)' }}>
            {serverError}
          </div>
        )}

        <form className="register-form" onSubmit={formik.handleSubmit}>
          <div className="register-form-row">
            <div className="register-form-col">
              <Input
                name="firstName"
                label="First Name"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && formik.errors.firstName}
                placeholder="Enter your first name"
                required
              />
            </div>
            <div className="register-form-col">
              <Input
                name="lastName"
                label="Last Name"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && formik.errors.lastName}
                placeholder="Enter your last name"
                required
              />
            </div>
          </div>

          <Input
            name="email"
            type="email"
            label="Email Address"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && formik.errors.email}
            placeholder="Enter your email"
            required
          />

          <Input
            name="password"
            type="password"
            label="Password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && formik.errors.password}
            placeholder="Create a password"
            required
          />

          <Input
            name="confirmPassword"
            type="password"
            label="Confirm Password"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && formik.errors.confirmPassword}
            placeholder="Confirm your password"
            required
          />

          <div className="register-terms">
            <label>
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={() => setAgreeToTerms(!agreeToTerms)}
              />{' '}
              I agree to the <Link to="#">Terms of Service</Link> and <Link to="#">Privacy Policy</Link>
            </label>
          </div>

          <Button
            type="submit"
            variant="primary"
            className="register-submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <div className="register-login">
          Already have an account? <Link to={routes.public.LOGIN}>Sign In</Link>
        </div>
      </div>
    </div>
  );
};

export default Register; 